import React, { useEffect, useRef, useState } from 'react';

import { formatNumber } from '../../helpers/formatNumber';
import useTranslation from '../../hooks/useTranslation';
import { fetchInvestmentsStatement } from '../../services/investmentsService';

const InfoIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" strokeLinecap="round" />
    <circle cx="12" cy="7.6" r="0.6" fill="currentColor" stroke="none" />
  </svg>
);

// Hover (desktop) + tap (mobile) tooltip. Closes on outside tap.
const InfoTip = ({ text }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [open]);

  return (
    <span className={`mc-tip${open ? ' mc-tip--open' : ''}`} ref={ref}>
      <button
        type="button"
        className="mc-tip__btn"
        aria-label={text}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}>
        <InfoIcon />
      </button>
      <span className="mc-tip__pop" role="tooltip">
        {text}
      </span>
    </span>
  );
};

const InvestmentsStatement = () => {
  const t = useTranslation();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const response = await fetchInvestmentsStatement();
        if (alive) setData(response);
      } catch (error) {
        console.error('Error loading investments summary:', error);
      } finally {
        if (alive) setIsLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (isLoading) {
    return <div className="mc-stmt mc-stmt--loading">…</div>;
  }
  if (!data) return null;

  const cur = data.currency || 'USDT';
  const invested = data.invested || 0;
  const returned = data.principal_returned || 0;
  const outstanding = data.principal_outstanding || 0;
  const payout = data.total_payout || 0;
  const result = data.net_result || 0;
  const pct = data.net_result_pct;

  const money = (n) => `${formatNumber(Math.round(n || 0), 0)} ${cur}`;
  const signSym = (n) => (n > 0 ? '+' : n < 0 ? '−' : '');
  const signedMoney = (n) => `${signSym(n)}${formatNumber(Math.abs(Math.round(n || 0)), 0)} ${cur}`;
  const pctStr =
    pct === null || pct === undefined ? '' : ` (${signSym(pct)}${formatNumber(Math.abs(pct), 1)}%)`;

  const retPct = invested > 0 ? Math.min(100, Math.max(0, (returned / invested) * 100)) : 0;
  const resultClass = result > 0 ? 'mc-pos' : result < 0 ? 'mc-neg' : '';

  return (
    <div className="mc-stmt">
      <p className="mc-stmt__title">{t('dashboard_inv_title')}</p>

      <div className="mc-stmt__rows">
        <div className="mc-stmt__row">
          <span className="mc-stmt__lbl">
            {t('dashboard_inv_invested')} <InfoTip text={t('dashboard_inv_tip_invested')} />
          </span>
          <span className="mc-stmt__val">{money(invested)}</span>
        </div>

        <div className="mc-stmt__row mc-stmt__row--minus">
          <span className="mc-stmt__lbl">
            − {t('dashboard_inv_returned')} <InfoTip text={t('dashboard_inv_tip_returned')} />
          </span>
          <span className="mc-stmt__val">{money(returned)}</span>
        </div>

        <div className="mc-stmt__row mc-stmt__row--key">
          <span className="mc-stmt__lbl">
            = {t('dashboard_inv_outstanding')} <InfoTip text={t('dashboard_inv_tip_outstanding')} />
          </span>
          <span className="mc-stmt__val">{money(outstanding)}</span>
        </div>

        <div className="mc-stmt__bar-wrap">
          <div className="mc-stmt__bar">
            <div className="mc-stmt__bar-fill" style={{ width: `${retPct}%` }} />
          </div>
          <div className="mc-stmt__legend">
            <span>
              <i className="mc-dot mc-dot--ret" />
              {t('dashboard_inv_returned_legend')} {formatNumber(Math.round(returned), 0)}
            </span>
            <span>
              <i className="mc-dot mc-dot--out" />
              {t('dashboard_inv_outstanding_legend')} {formatNumber(Math.round(outstanding), 0)}
            </span>
          </div>
        </div>

        <div className="mc-stmt__row">
          <span className="mc-stmt__lbl">
            {t('dashboard_inv_payout')} <InfoTip text={t('dashboard_inv_tip_payout')} />
          </span>
          <span className="mc-stmt__val">{money(payout)}</span>
        </div>

        <div className="mc-stmt__row mc-stmt__row--total">
          <span className="mc-stmt__lbl">
            = {t('dashboard_inv_result')} <InfoTip text={t('dashboard_inv_tip_result')} />
          </span>
          <span className={`mc-stmt__val ${resultClass}`}>
            {signedMoney(result)}
            {pctStr ? <span className="mc-stmt__pct">{pctStr}</span> : null}
          </span>
        </div>
      </div>
    </div>
  );
};

export default React.memo(InvestmentsStatement);
